import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/analytics';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CompanyContractAcceptanceForm } from '@/components/legal/CompanyContractAcceptanceForm';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

export default function CreateCompany() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isCompanyMember, isLoading: isCompanyLoading } = useCompany();
  const queryClient = useQueryClient();

  const [companyName, setCompanyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdCompanyId, setCreatedCompanyId] = useState<string | null>(null);

  // If already a company member, redirect to dashboard
  if (!isCompanyLoading && isCompanyMember) {
    return <Navigate to="/app/company" replace />;
  }

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = companyName.trim();
    if (!trimmedName || !user?.id) return;

    if (trimmedName.length > 100) {
      toast.error(t('company.nameTooLong'));
      return;
    }

    setIsCreating(true);

    try {
      // 1. Create the company
      const { data: company, error: companyError } = await supabase
        .from('care_companies')
        .insert({ name: trimmedName, owner_id: user.id })
        .select('id')
        .single();

      if (companyError) throw companyError;

      // 2. Add the creator as manager (first member)
      const { error: memberError } = await supabase
        .from('company_members')
        .insert({
          company_id: company.id,
          user_id: user.id,
          role: 'manager',
        });

      if (memberError) throw memberError;

      // Refresh company membership cache so banner/hooks know the new company
      await queryClient.invalidateQueries({ queryKey: ['company-membership'] });

      // 3. Move on to the contract acceptance step
      setCreatedCompanyId(company.id);
      track('company_created', { company_id: company.id });
      toast.success(t('company.createSuccess'));
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error(t('company.createError'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleAcceptanceSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['company-membership'] });
    navigate('/app/company', { replace: true });
  };

  if (isCompanyLoading) {
    return (
      <AppLayout title={t('company.createTitle')} showBack>
        <div className="flex items-center justify-center p-8">
          <CareOSLoader size="md" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={t('company.createTitle')} showBack>
      <div className="p-4 space-y-6 max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
            <Building2 className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-xl font-bold">{t('company.createTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('company.createDescription')}</p>
        </div>

        {/* Step 1: Company info */}
        {!createdCompanyId && (
          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleCreateCompany} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">{t('company.companyName')}</Label>
                  <Input
                    id="companyName"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder={t('company.companyNamePlaceholder')}
                    maxLength={100}
                    required
                    autoFocus
                  />
                </div>

                <p className="text-xs text-muted-foreground">
                  Próximo passo: aceite do Contrato B2B e DPA pelo representante da empresa.
                </p>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isCreating || !companyName.trim()}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    'Continuar'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Contract acceptance */}
        {createdCompanyId && (
          <Card>
            <CardContent className="p-6">
              <CompanyContractAcceptanceForm
                companyId={createdCompanyId}
                submitLabel="Concluir cadastro"
                onSuccess={handleAcceptanceSuccess}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
